import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

type YhRichTextEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

/** Quill editor — React 19 safe (no react-quill / findDOMNode). */
const YhRichTextEditor: React.FC<YhRichTextEditorProps> = ({
  value = "",
  onChange,
  placeholder,
  className = "",
  style,
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const skipSync = useRef(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!hostRef.current || quillRef.current) return;

    const quill = new Quill(hostRef.current, {
      theme: "snow",
      placeholder: placeholder || "",
      modules: {
        toolbar: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
      },
    });
    quillRef.current = quill;

    if (value) {
      quill.root.innerHTML = value;
    }

    quill.on("text-change", () => {
      skipSync.current = true;
      onChangeRef.current?.(quill.root.innerHTML);
    });

    return () => {
      quillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    if ((value || "") !== quill.root.innerHTML) {
      const sel = quill.getSelection();
      quill.root.innerHTML = value || "";
      if (sel) {
        try {
          quill.setSelection(sel);
        } catch {
          /* ignore */
        }
      }
    }
  }, [value]);

  return (
    <div className={`yh-rich-text-editor ${className}`} style={style}>
      <div ref={hostRef} style={{ minHeight: 160 }} />
    </div>
  );
};

export default YhRichTextEditor;
